import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import { Calendar, IdCard, Clipboard, Search, TriangleAlert, SquareCheckBig, Target, Folder, Plus, Phone, File, Eye, Save, Rocket, Paperclip, Mail, ListOrdered, MoveUp, MoveDown, Settings, Trash, RefreshCcw, Edit, Wrench } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';

const FormEditor = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [tramites, setTramites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showCreateNew, setShowCreateNew] = useState(false);

  const { success, error } = useNotifications();

  const [busquedaForm, setbusquedaForm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  // Verificar permisos de admin
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acceso Denegado</h2>
          <p className="text-gray-600">Solo los administradores pueden editar formularios.</p>
        </div>
      </div>
    );
  }

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      let forms = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/form`, { headers });
      let types = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/type`, { headers });
      let categories = await fetch(`${process.env.REACT_APP_BACKEND_URL}/categories`, { headers });

      if (forms.ok) {
        setTemplates(await forms.json());
      }

      if (types.ok) {
        setTramites(await types.json());
      }

      if (categories.ok) {
        setCategories(await categories.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowEditor(true);
    setShowCreateNew(false);
  };

  const handleCreateNew = () => {
    setSelectedTemplate(null);
    setShowEditor(true);
    setShowCreateNew(true);
  };

  const handleAutoGenerateAll = async () => {
    if (window.confirm('¿Estás seguro de auto-generar formularios para todos los trámites sin formulario? Esta acción creará formularios básicos automáticamente.')) {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/auto-generate-forms`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const result = await response.json();
          success(<SquareCheckBig /> + `${result.summary}`);
          fetchData();
        } else {
          const error = await response.json();
          success(`Error: ${error.detail}`);
        }
      } catch (error) {
        success('Error generando formularios automáticamente');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRegenerateTemplate = async (template) => {
    if (window.confirm(`¿Regenerar formulario automático para "${template.tramite_name}"? Esto sobrescribirá el formulario actual con uno basado en los documentos requeridos.`)) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/applications/admin/applications/${template.tramite_type_id}/regenerate-form`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const result = await response.json();
          success(<SquareCheckBig /> + `${result.message}`);
          fetchData();
        } else {
          const error = await response.json();
          success(`Error: ${error.detail}`);
        }
      } catch (error) {
        success('Error regenerando formulario');
      }
    }
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setShowCreateNew(false);
    setSelectedTemplate(null);
    fetchData(); // Refresh data
  };

  const handleDeleteTemplate = async (templateId) => {
    if (window.confirm('¿Estás seguro de eliminar este formulario? Esta acción no se puede deshacer.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/form/${templateId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          success('Formulario eliminado exitosamente');
          fetchData();
        } else {
          const error = await response.json();
          success(`Error: ${error.detail}`);
        }
      } catch (error) {
        success('Error eliminando formulario');
      }
    }
  };

  const handleTogglePublish = async (templateId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/form/${templateId}/publish`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ publish: !currentStatus })
      });

      if (response.ok) {
        const action = !currentStatus ? 'publicado' : 'despublicado';
        success(`Formulario ${action} exitosamente`);
        fetchData();
      } else {
        const error = await response.json();
        success(`Error: ${error.detail}`);
      }
    } catch (error) {
      success('Error actualizando estado');
    }
  };

  if (loading) {
    return <LoadingSpinner message="Cargando editor de formularios..." />;
  }

  if (showEditor) {
    return (
      <FormTemplateEditor
        template={selectedTemplate}
        tramites={tramites}
        categories={categories}
        isCreating={showCreateNew}
        onClose={handleCloseEditor}
      />
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Editor de Formularios</h1>
          <p className="text-gray-600 mt-2">
            Crea y gestiona formularios personalizados para cada trámite
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleAutoGenerateAll}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            Auto-Generar Todos
          </button>
          <button
            onClick={handleCreateNew}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex row items-center"
          >
            <Plus className="mr-1" />Nuevo Formulario
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatCard title="Total Formularios" value={templates.length} icon={<Clipboard />} color="blue" />
        <StatCard
          title="Publicados"
          value={templates.filter(t => t.is_published).length}
          icon={<SquareCheckBig />}
          color="green"
        />
        <StatCard
          title="Borradores"
          value={templates.filter(t => !t.is_published).length}
          icon={<Clipboard />}
          color="yellow"
        />
        <StatCard title="Trámites Sin Formulario" value={tramites.length - templates.length} icon={<TriangleAlert />} color="red" />
      </div>

      {/* Campo de búsqueda por Form ID */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex row items-center">
              <Search className='mr-1' />Buscar Formulario
            </label>
            <input
              type="text"
              value={busquedaForm}
              onChange={(e) => setbusquedaForm(e.target.value)}
              placeholder="Buscar en formularios..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="mt-6">
            <button
              onClick={() => setbusquedaForm('')}
              className="bg-gray-500 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Limpiar
            </button>
          </div>
        </div>

        {busquedaForm && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <Search /> Buscando formularios que contengan: <strong>{busquedaForm}</strong>
            </p>
          </div>
        )}
      </div>

      {/* Templates Grid - Con filtro por Form ID */}
      <div className="grid gap-6">
        {templates.length > 0 ? (
          templates
            .filter(template => {
              if (busquedaForm) {
                return template.name.includes(busquedaForm) || template.description.includes(busquedaForm);
              }
              return true;
            })
            .map((template) => (
              <TemplateCard
                key={template._id}
                template={template}
                onEdit={() => handleEditTemplate(template)}
                onDelete={() => handleDeleteTemplate(template._id)}
                onTogglePublish={() => handleTogglePublish(template._id, template.is_published)}
                onRegenerate={() => handleRegenerateTemplate(template)}
                destacado={busquedaForm && template._id?.substring(0, 7).toLowerCase().includes(busquedaForm.toLowerCase())}
              />
            ))
        ) : (
          <div className="text-center py-16 bg-white rounded-lg shadow">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No hay formularios creados
            </h3>
            <p className="text-gray-500 mb-6">
              Crea tu primer formulario personalizado para comenzar
            </p>
            <button
              onClick={handleCreateNew}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Crear Primer Formulario
            </button>
          </div>
        )}

        {/* Mensaje si no hay resultados de búsqueda */}
        {busquedaForm && templates.filter(template => {
          const templateId = template._id?.substring(0, 7) || '';
          return templateId.toLowerCase().includes(busquedaForm.toLowerCase());
        }).length === 0 && (
            <div className="text-center py-16 bg-white rounded-lg shadow border-2 border-dashed border-gray-300">
              <div className="text-6xl mb-4"><Search /></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No se encontró Form ID: {busquedaForm}
              </h3>
              <p className="text-gray-500 mb-6">
                Verifica que el Form ID sea correcto. Los IDs de formulario son únicos de 7 caracteres.
              </p>
              <button
                onClick={() => setbusquedaForm('')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                🗑️ Limpiar Búsqueda
              </button>
            </div>
          )}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    red: 'bg-red-50 text-red-600 border-red-200'
  };

  return (
    <div className={`p-6 rounded-2xl border-2 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
    </div>
  );
};

const TemplateCard = ({ template, onEdit, onDelete, onTogglePublish, onRegenerate, destacado = false }) => {
  const isAutoGenerated = template.name?.startsWith('Formulario Auto:');

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all ${isAutoGenerated ? 'border-l-4 border-purple-500' : ''
      } ${destacado ? 'ring-4 ring-yellow-300 bg-yellow-50 transform scale-105' : ''
      }`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-xl font-semibold text-gray-900">{template.name}</h3>
            {destacado && (
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-200 text-yellow-800 animate-pulse">
                <Target /> ENCONTRADO
              </span>
            )}
            {isAutoGenerated && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Auto-generado
              </span>
            )}
            <span className={`px-3 py-1 rounded-full text-xs font-medium flex row items-center gap-2 ${template.is_published
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
              }`}>
              <SquareCheckBig /> {template.is_published ? ' Publicado' : (<Clipboard /> && 'Borrador')}
            </span>
            <span className="text-xs text-gray-500">v{template.version}</span>
          </div>

          {/* Form ID único - más prominente si está destacado */}
          <div className="mb-3">
            <span className={`text-sm font-mono px-3 py-2 rounded border flex row items-center gap-2 ${destacado
              ? 'bg-yellow-200 text-yellow-900 border-yellow-400 font-bold'
              : 'bg-blue-100 text-blue-800 border-blue-200'
              }`}>
              <IdCard /> Form ID: {template._id?.substring(0, 7) || 'N/A'}
            </span>
          </div>

          <p className="text-gray-600 mb-3">{template.description}</p>

          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <span class="flex row items-center gap-2"><Target /> {template.tramite_name}</span>
            <span class="flex row items-center gap-2"><Folder /> {template.category_name}</span>
            <span class="flex row items-center gap-2"><Wrench /> {template.fields?.length || 0} campos</span>
            <span class="flex row items-center gap-2"><Paperclip /> {template.fields?.filter(f => f.type === 'file').length || 0} archivos</span>
            <span class="flex row items-center gap-2"><Calendar /> {new Date(template.updated_at).toLocaleDateString()}</span>
          </div>
        </div>

        <div className="flex space-x-2 ml-4">
          {isAutoGenerated && (
            <button
              onClick={onRegenerate}
              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              title="Regenerar desde documentos"
            >
              <RefreshCcw />
            </button>
          )}
          <button
            onClick={onEdit}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Editar"
          >
            <Edit />
          </button>
          <button
            onClick={onTogglePublish}
            className={`p-2 rounded-lg transition-colors ${template.is_published
              ? 'text-orange-600 hover:bg-orange-50'
              : 'text-green-600 hover:bg-green-50'
              }`}
            title={template.is_published ? 'Despublicar' : 'Publicar'}
          >
            {template.is_published ? <Eye /> : <Rocket />}
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Eliminar"
          >
            <Trash />
          </button>
        </div>
      </div>

      {isAutoGenerated && (
        <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center text-sm text-purple-800">
            <span className="mr-2">🤖</span>
            <span>
              Este formulario fue generado automáticamente basado en los documentos requeridos del trámite.
              Puedes editarlo para agregar/quitar campos según tu experiencia operativa.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const FormTemplateEditor = ({ template, tramites, categories, isCreating, onClose }) => {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    description: template?.description || '',
    tramite_type_id: template?.tramite_type_id || '',
    fields: template?.fields || []
  });
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedFieldType, setSelectedFieldType] = useState('text');
  const [showAddField, setShowAddField] = useState(false);
  const { success, error } = useNotifications();

  const fieldTypes = [
    { value: 'text', label: 'Texto', icon: <Clipboard /> },
    { value: 'email', label: 'Email', icon: <Mail /> },
    { value: 'phone', label: 'Teléfono', icon: <Phone /> },
    { value: 'textarea', label: 'Área de Texto', icon: <File /> },
    { value: 'select', label: 'Lista Desplegable', icon: <Clipboard /> },
    { value: 'checkbox', label: 'Casilla', icon: <SquareCheckBig /> },
    { value: 'number', label: 'Número', icon: <ListOrdered /> },
    { value: 'date', label: 'Fecha', icon: <Calendar /> },
    // { value: 'file', label: 'Archivo Adjunto', icon: <Paperclip /> }
  ];

  const handleSave = async () => {
    if (!formData.name.trim()) {
      success('El nombre del formulario es requerido');
      return;
    }

    if (!formData.tramite_type_id) {
      success('Debes seleccionar un trámite');
      return;
    }

    if (formData.fields.length === 0) {
      success('Debes agregar al menos un campo');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = isCreating
        ? `${process.env.REACT_APP_BACKEND_URL}/admin/form`
        : `${process.env.REACT_APP_BACKEND_URL}/admin/form/${template._id}`;

      const method = isCreating ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        success(`Formulario ${isCreating ? 'creado' : 'actualizado'} exitosamente`);
        onClose();
      } else {
        const error = await response.json();
        success(`Error: ${error.detail}`);
      }
    } catch (error) {
      success('Error guardando formulario');
    } finally {
      setSaving(false);
    }
  };

  const addField = (type) => {
    const newField = {
      id: `field_${Date.now()}`,
      name: `campo_${formData.fields.length + 1}`,
      label: `Campo ${formData.fields.length + 1}`,
      type: type,
      required: false,
      placeholder: '',
      order: formData.fields.length,
      validation_rules: {}
    };

    setFormData({
      ...formData,
      fields: [...formData.fields, newField]
    });
    setShowAddField(false);
  };

  const updateField = (fieldId, updates) => {
    setFormData({
      ...formData,
      fields: formData.fields.map(field =>
        field.id === fieldId ? { ...field, ...updates } : field
      )
    });
  };

  const removeField = (fieldId) => {
    if (window.confirm('¿Eliminar este campo?')) {
      setFormData({
        ...formData,
        fields: formData.fields.filter(field => field.id !== fieldId)
      });
    }
  };

  const moveField = (fieldId, direction) => {
    const currentIndex = formData.fields.findIndex(f => f.id === fieldId);
    if ((direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === formData.fields.length - 1)) {
      return;
    }

    const newFields = [...formData.fields];
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    [newFields[currentIndex], newFields[targetIndex]] = [newFields[targetIndex], newFields[currentIndex]];

    // Update order
    newFields.forEach((field, index) => {
      field.order = index;
    });

    setFormData({ ...formData, fields: newFields });
  };

  if (showPreview) {
    return (
      <FormPreview
        formData={formData}
        onClose={() => setShowPreview(false)}
        tramites={tramites}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={onClose}
              className="flex items-center text-blue-600 hover:text-blue-800 mb-4 font-medium"
            >
              ← Volver al Editor
            </button>
            <h1 className="text-3xl font-bold text-gray-900">
              {isCreating ? 'Crear Nuevo Formulario' : 'Editar Formulario'}
            </h1>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowPreview(true)}
              className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors font-medium flex row items-center gap-1"
            >
              <Eye /> Vista Previa
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Configuration Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Configuración</h3>

              {/* Basic Info */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Formulario *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ej: Formulario de Avalúos"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-20"
                    placeholder="Describe el propósito de este formulario..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trámite Asociado *
                  </label>
                  <select
                    value={formData.tramite_type_id}
                    onChange={(e) => setFormData({ ...formData, tramite_type_id: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Selecciona un trámite...</option>
                    {tramites.map((tramite) => {
                      const category = categories.find(c => c.slug === tramite.category);
                      return (
                        <option key={tramite._id} value={tramite._id}>
                          {category?.name || 'Sin categoría'} - {tramite.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Add Field Button */}
              <button
                onClick={() => setShowAddField(!showAddField)}
                className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium flex row items-center gap-2 justify-center"
              >
                <Plus /> Agregar Campo
              </button>

              {/* Field Type Selector */}
              {showAddField && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Tipo de Campo</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {fieldTypes.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => addField(type.value)}
                        className="p-3 bg-white border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-left flex row items-center gap-2"
                      >
                        <div className="text-lg mb-1">{type.icon}</div>
                        <div className="text-sm font-medium">{type.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Form Builder */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Constructor de Formulario ({formData.fields.length} campos)
                </h3>
              </div>

              {formData.fields.length > 0 ? (
                <div className="space-y-4">
                  {formData.fields.map((field, index) => (
                    <FieldEditor
                      key={field.id}
                      field={field}
                      index={index}
                      totalFields={formData.fields.length}
                      onUpdate={(updates) => updateField(field.id, updates)}
                      onRemove={() => removeField(field.id)}
                      onMove={(direction) => moveField(field.id, direction)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-lg">
                  <div className="text-6xl mb-4">📋</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Formulario Vacío
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Agrega campos usando el panel lateral para comenzar a construir tu formulario
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FieldEditor = ({ field, index, totalFields, onUpdate, onRemove, onMove }) => {
  const [expanded, setExpanded] = useState(false);

  const fieldTypeInfo = {
    text: { icon: <Clipboard />, label: 'Texto' },
    email: { icon: <Mail />, label: 'Email' },
    phone: { icon: <Phone />, label: 'Teléfono' },
    textarea: { icon: <File />, label: 'Área de Texto' },
    select: { icon: <Clipboard />, label: 'Lista' },
    checkbox: { icon: <SquareCheckBig />, label: 'Casilla' },
    number: { icon: <ListOrdered />, label: 'Número' },
    date: { icon: <Calendar />, label: 'Fecha' }
  };

  const info = fieldTypeInfo[field.type] || { icon: '❓', label: 'Desconocido' };

  return (
    <div className="border border-gray-200 rounded-lg">
      {/* Field Header */}
      <div className="flex items-center justify-between p-4 bg-gray-50">
        <div className="flex items-center space-x-3">
          <span className="text-xl">{info.icon}</span>
          <div>
            <h4 className="font-medium text-gray-900">{field.label || 'Campo sin título'}</h4>
            <p className="text-sm text-gray-500">
              {info.label} • {field.required ? 'Requerido' : 'Opcional'}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Move buttons */}
          <button
            onClick={() => onMove('up')}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            title="Mover arriba"
          >
            <MoveUp />
          </button>
          <button
            onClick={() => onMove('down')}
            disabled={index === totalFields - 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            title="Mover abajo"
          >
            <MoveDown />
          </button>

          {/* Edit button */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Configurar"
          >
            <Settings />
          </button>

          {/* Remove button */}
          <button
            onClick={onRemove}
            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Eliminar"
          >
            <Trash />
          </button>
        </div>
      </div>

      {/* Field Configuration (expandable) */}
      {expanded && (
        <div className="p-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Etiqueta *
              </label>
              <input
                type="text"
                value={field.label}
                onChange={(e) => onUpdate({ label: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Etiqueta del campo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del Campo *
              </label>
              <input
                type="text"
                value={field.name}
                onChange={(e) => onUpdate({ name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="nombre_del_campo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Placeholder
              </label>
              <input
                type="text"
                value={field.placeholder || ''}
                onChange={(e) => onUpdate({ placeholder: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Texto de ayuda"
              />
            </div>

            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(e) => onUpdate({ required: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Requerido</span>
              </label>
            </div>

            {/* Options for select fields */}
            {field.type === 'select' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Opciones (una por línea)
                </label>
                <textarea
                  value={(field.options || []).join('\n')}
                  onChange={(e) => onUpdate({
                    options: e.target.value.split('\n').filter(opt => opt.trim())
                  })}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24"
                  placeholder="Opción 1&#10;Opción 2&#10;Opción 3"
                />
              </div>
            )}

            {/* Configuration for file fields */}
            {field.type === 'file' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipos de Archivo Permitidos
                </label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={(field.file_types || []).includes('pdf')}
                        onChange={(e) => {
                          const currentTypes = field.file_types || [];
                          const newTypes = e.target.checked
                            ? [...currentTypes.filter(t => t !== 'pdf'), 'pdf']
                            : currentTypes.filter(t => t !== 'pdf');
                          onUpdate({ file_types: newTypes });
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">📄 PDF</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={(field.file_types || []).includes('image')}
                        onChange={(e) => {
                          const currentTypes = field.file_types || [];
                          const newTypes = e.target.checked
                            ? [...currentTypes.filter(t => t !== 'image'), 'image']
                            : currentTypes.filter(t => t !== 'image');
                          onUpdate({ file_types: newTypes });
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">🖼️ Imágenes (JPG, PNG)</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tamaño Máximo (MB)
                    </label>
                    <input
                      type="number"
                      value={field.max_file_size || 10}
                      onChange={(e) => onUpdate({ max_file_size: parseInt(e.target.value) || 10 })}
                      className="w-32 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="1"
                      max="100"
                    />
                  </div>

                  <div className="text-xs text-gray-500">
                    💡 Los archivos se validarán automáticamente según los tipos seleccionados
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const FormPreview = ({ formData, onClose, tramites }) => {
  const tramite = tramites.find(t => t.id === formData.tramite_type_id);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={onClose}
              className="flex items-center text-blue-600 hover:text-blue-800 mb-4 font-medium"
            >
              ← Volver al Editor
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Vista Previa</h1>
            <p className="text-gray-600">{formData.name}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 flex row items-center gap-2">
              <Clipboard /> {formData.name}
            </h2>
            {formData.description && (
              <p className="text-gray-600 mb-4">{formData.description}</p>
            )}
            {tramite && (
              <div className="text-sm text-gray-500">
                Trámite: {tramite.name}
              </div>
            )}
          </div>

          <form className="space-y-6">
            {formData.fields.map((field) => (
              <PreviewField key={field.id} field={field} />
            ))}

            <div className="flex space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex row items-center gap-2"
              >
                <Save />Guardar Progreso
              </button>
              <button
                type="button"
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex row items-center gap-2"
              >
                <Rocket /> Enviar Solicitud
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const PreviewField = ({ field }) => {
  const renderField = () => {
    const baseClasses = "w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent";

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <input
            type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
            placeholder={field.placeholder}
            className={baseClasses}
            disabled
          />
        );

      case 'textarea':
        return (
          <textarea
            placeholder={field.placeholder}
            className={`${baseClasses} h-24 resize-none`}
            disabled
          />
        );

      case 'select':
        return (
          <select className={baseClasses} disabled>
            <option value="">Selecciona una opción...</option>
            {(field.options || []).map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              disabled
            />
            <span className="text-sm text-gray-700">{field.placeholder || 'Acepto'}</span>
          </div>
        );

      case 'number':
        return (
          <input
            type="number"
            placeholder={field.placeholder}
            className={baseClasses}
            disabled
          />
        );

      case 'date':
        return (
          <input
            type="date"
            className={baseClasses}
            disabled
          />
        );

      case 'file':
        const fileTypes = field.file_types || ['pdf', 'image'];
        const acceptTypes = fileTypes.includes('pdf') && fileTypes.includes('image')
          ? '.pdf,.jpg,.jpeg,.png'
          : fileTypes.includes('pdf')
            ? '.pdf'
            : '.jpg,.jpeg,.png';

        return (
          <div className="space-y-2">
            <input
              type="file"
              accept={acceptTypes}
              className={`${baseClasses} file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100`}
              disabled
            />
            <div className="text-xs text-gray-500 space-y-1">
              <p>📎 Tipos permitidos: {fileTypes.map(type => type === 'pdf' ? 'PDF' : 'Imágenes (JPG, PNG)').join(', ')}</p>
              <p>📏 Tamaño máximo: {field.max_file_size || 10} MB</p>
              <p>💡 {field.placeholder || 'Selecciona un archivo para subir'}</p>
            </div>
          </div>
        );

      default:
        return (
          <input
            type="text"
            placeholder={field.placeholder}
            className={baseClasses}
            disabled
          />
        );
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {renderField()}
    </div>
  );
};

export default FormEditor;